import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { DayTime } from '../../../types/day-time.type';
import type { AgendaItemModel } from '../models/middleware/agenda-item.model';

export interface TimeStatus {
  isPast: boolean;
  isCurrent: boolean;
  isFuture: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CurrentTimeService {
  private currentTime$ = new BehaviorSubject<Date>(new Date());

  constructor() {
    // Update current time every minute
    interval(60000).subscribe(() => {
      this.currentTime$.next(new Date());
    });
  }

  getCurrentTime(): Observable<Date> {
    return this.currentTime$.asObservable();
  }

  getCurrentDayTime(): Observable<DayTime> {
    return this.currentTime$.pipe(map((time) => this.getDayTimeFromHour(time.getHours())));
  }

  getItemTimeStatus(item: AgendaItemModel): Observable<TimeStatus> {
    return this.currentTime$.pipe(
      map((currentTime) => {
        const now = currentTime.getTime();
        const itemStart = item.startTime.getTime();
        const itemEnd = item.endTime.getTime();

        return {
          isPast: now > itemEnd,
          isCurrent: now >= itemStart && now <= itemEnd,
          isFuture: now < itemStart,
        };
      })
    );
  }

  getDayTimeStatus(dayTime: DayTime, currentDay?: number, itemDay?: number): Observable<TimeStatus> {
    return this.currentTime$.pipe(
      map((currentTime) => {
        const currentHour = currentTime.getHours();
        const currentDayTime = this.getDayTimeFromHour(currentHour);

        // If we have day information, check if it's the current day
        if (currentDay !== undefined && itemDay !== undefined) {
          if (itemDay < currentDay) {
            return { isPast: true, isCurrent: false, isFuture: false };
          }
          if (itemDay > currentDay) {
            return { isPast: false, isCurrent: false, isFuture: true };
          }
        }

        // Same day logic
        const dayTimeOrder: DayTime[] = ['Vormittag', 'Mittag', 'Nachmittag', 'Abend'];
        const currentIndex = dayTimeOrder.indexOf(currentDayTime);
        const itemIndex = dayTimeOrder.indexOf(dayTime);

        return {
          isPast: itemIndex < currentIndex,
          isCurrent: itemIndex === currentIndex,
          isFuture: itemIndex > currentIndex,
        };
      })
    );
  }

  private getDayTimeFromHour(hour: number): DayTime {
    if (hour >= 0 && hour < 12) return 'Vormittag';
    if (hour >= 12 && hour < 14) return 'Mittag';
    if (hour >= 14 && hour < 18) return 'Nachmittag';
    return 'Abend';
  }
}
